<template>
    <!-- Header -->
    <div class="flex justify-between items-center pb-3">
        <h4 class="font-bold mb-0">{{ isEditMode ? t('calendar.edit.title') : t('calendar.new.title') }}</h4>
    </div>
    <!-- Single Card Container -->
    <div class="card flex flex-col overflow-hidden" style="height: calc(100vh - 9rem)">
        <!-- Loading State -->
        <div v-if="loading" class="flex-1 flex items-center justify-center">
            <div class="w-full max-w-3xl space-y-6 px-6">
                <Skeleton width="100%" height="56px" borderRadius="12px" />
                <Skeleton width="100%" height="320px" borderRadius="16px" />
                <Skeleton width="100%" height="48px" borderRadius="8px" />
            </div>
        </div>

        <template v-else>
            <!-- Stepper Header -->
            <div class="border-b border-surface-200 shrink-0 px-4 sm:px-6 py-2">
                <!-- Stepper Progress Indicator -->
                <div class="max-w-2xl mx-auto mb-2 sm:mb-3">
                    <div class="flex items-center">
                        <template v-for="(step, index) in steps" :key="step.id">
                            <button
                                class="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 shrink-0"
                                :class="[stepCircleClasses(index), index <= currentStepIndex ? 'cursor-pointer hover:shadow-md' : 'cursor-default']"
                                :disabled="index > currentStepIndex"
                                :aria-label="step.label"
                                @click="index <= currentStepIndex && goToStepById(step.id)"
                            >
                                <i v-if="index < currentStepIndex" class="fa-solid fa-check text-xs sm:text-sm"></i>
                                <span v-else class="text-xs sm:text-sm font-bold">{{ index + 1 }}</span>
                            </button>
                            <div v-if="index < steps.length - 1" class="flex-1 h-0.5 mx-1 sm:mx-2 transition-colors duration-500" :class="index < currentStepIndex ? 'bg-blue-600' : 'bg-surface-200'"></div>
                        </template>
                    </div>
                </div>

                <!-- Current Step Info -->
                <div class="text-center">
                    <p class="text-xs text-surface-400 hidden sm:block">
                        {{ t('calendar.new.step', { current: currentStepIndex + 1, total: steps.length }) }}
                    </p>
                    <h4 class="text-base sm:text-lg font-semibold mt-0.5 flex items-center justify-center gap-2">
                        {{ currentStep?.label }}
                    </h4>
                </div>
            </div>

            <!-- Scrollable Step Content -->
            <div class="flex-1 p-3 sm:p-6" :class="currentStepId === 'resources' ? 'overflow-y-auto lg:overflow-hidden' : 'overflow-y-auto'">
                <transition :name="transitionName" mode="out-in">
                    <!-- STEP: Reservation Type -->
                    <div v-if="currentStepId === 'type'" key="type" class="max-w-3xl sm:max-w-[70%] mx-auto">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                            <div
                                v-if="canManageOwnReservations"
                                class="p-4 sm:p-5 rounded-xl border-2 cursor-pointer transition-all"
                                :class="hasVisitors ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-surface-200 hover:border-blue-300 hover:bg-blue-50/30'"
                                @click="switchToExternal"
                            >
                                <div class="flex items-center gap-3 md:flex-col md:items-center md:text-center md:gap-3 md:py-4">
                                    <div class="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-blue-100 flex items-center justify-center shrink-0">
                                        <i class="fa-solid fa-users text-blue-600 text-lg md:text-2xl"></i>
                                    </div>
                                    <div class="flex-1 md:flex-none min-w-0">
                                        <h4 class="font-semibold text-base md:text-lg md:mb-1">
                                            {{ t('calendar.new.reservationType.visit') }}
                                        </h4>
                                        <p class="text-sm text-surface-500">
                                            {{ t('calendar.new.reservationType.visitDesc') }}
                                        </p>
                                    </div>
                                    <i v-if="hasVisitors" class="fa-solid fa-check-circle text-blue-500 text-xl shrink-0"></i>
                                </div>
                            </div>

                            <div
                                v-if="canManageInternalReservations"
                                class="p-4 sm:p-5 rounded-xl border-2 cursor-pointer transition-all"
                                :class="!hasVisitors ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-surface-200 hover:border-purple-300 hover:bg-purple-50/30'"
                                @click="switchToInternal"
                            >
                                <div class="flex items-center gap-3 md:flex-col md:items-center md:text-center md:gap-3 md:py-4">
                                    <div class="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-purple-100 flex items-center justify-center shrink-0">
                                        <i class="fa-regular fa-building text-purple-600 text-lg md:text-2xl"></i>
                                    </div>
                                    <div class="flex-1 md:flex-none min-w-0">
                                        <h4 class="font-semibold text-base md:text-lg md:mb-1">
                                            {{ t('calendar.new.reservationType.internal') }}
                                        </h4>
                                        <p class="text-sm text-surface-500">
                                            {{ t('calendar.new.reservationType.internalDesc') }}
                                        </p>
                                    </div>
                                    <i v-if="!hasVisitors" class="fa-solid fa-check-circle text-purple-500 text-xl shrink-0"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- STEP: Visitor Data -->
                    <div v-else-if="currentStepId === 'visitor'" key="visitor" class="max-w-[90%] mx-auto">
                        <VisitorDataForm
                            v-model:visitor="form.visitor"
                            v-model:selectedErpCustomer="selectedErpCustomer"
                            :countries="countriesStore.countries"
                            :countries-loading="countriesStore.loading"
                            :commercial-segments="commercialSegmentsStore.commercialSegments"
                            :commercial-segments-loading="commercialSegmentsStore.loading"
                            :validation-errors="visitorValidationErrors"
                            @touch-field="touchField"
                        />
                    </div>

                    <!-- STEP: Reservation Details -->
                    <div v-else-if="currentStepId === 'details'" key="details" class="max-w-[90%] mx-auto">
                        <div class="space-y-4">
                            <div class="flex flex-col gap-2">
                                <label for="title" class="font-medium">
                                    {{ t('calendar.new.reservation.title') }}
                                    <span class="text-red-500">*</span>
                                </label>
                                <InputText id="title" type="text" v-model="form.title" :placeholder="t('calendar.new.reservation.titlePlaceholder')" class="w-full" @blur="touched.title = true" maxlength="255" />
                                <small v-if="validation.title.value.invalid" class="text-red-500">
                                    {{ validation.title.value.message }}
                                </small>
                            </div>

                            <div class="flex flex-col gap-2">
                                <label for="description" class="font-medium">
                                    {{ t('calendar.new.reservation.description') }}
                                </label>
                                <Textarea id="description" rows="3" v-model="form.description" :placeholder="t('calendar.new.reservation.descriptionPlaceholder')" class="w-full" maxlength="255" />
                                <small v-if="validation.description.value.invalid" class="text-red-500">
                                    {{ validation.description.value.message }}
                                </small>
                            </div>

                            <div class="flex flex-col gap-2">
                                <label for="attendees" class="font-medium">
                                    {{ t('calendar.new.reservation.attendees') }}
                                    <span class="text-red-500">*</span>
                                </label>
                                <InputNumber id="attendees" v-model="form.attendees" :min="1" :useGrouping="false" showButtons placeholder="0" fluid @blur="touched.attendees = true" />
                                <small v-if="validation.attendees.value.invalid" class="text-red-500">
                                    {{ validation.attendees.value.message }}
                                </small>
                            </div>
                        </div>
                    </div>

                    <!-- STEP: Resources & Slots -->
                    <div v-else-if="currentStepId === 'resources'" key="resources" class="lg:h-full">
                        <ResourcesSlotsForm
                            v-model:selected-date="form.date"
                            :selected-resource-ids="form.selectedResourceIds"
                            :resources="resourcesStore.resources"
                            :resources-loading="resourcesStore.loading"
                            :resource-slot-configs="resourceSlotConfigs"
                            :slot-capacities="slotCapacities"
                            :attendees="form.attendees"
                            :editing-reservation-id="editingReservationId"
                            @update:selected-resource-ids="handleResourceSelectionChange"
                            @update:resource-slot-config="handleResourceSlotConfigUpdate"
                            @remove-resource="handleRemoveResource"
                            @slot-validation-errors-change="(hasErrors: boolean) => (slotConflictError = hasErrors ? 'slot-overlap' : null)"
                        />
                    </div>

                    <!-- STEP: Summary / Review -->
                    <div v-else-if="currentStepId === 'summary'" key="summary" class="max-w-[90%] mx-auto">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                            <!-- ===== LEFT PANEL: Type + Customer + Details ===== -->
                            <div class="space-y-4">
                                <!-- Section A: Reservation Type -->
                                <div>
                                    <div class="flex items-center justify-between mb-2">
                                        <span class="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                                            {{ t('calendar.new.summary.type') }}
                                        </span>
                                        <Button v-if="!isEditMode" icon="fa-solid fa-pen" text rounded size="small" :aria-label="t('edit')" @click="goToStepById('type')" />
                                    </div>
                                    <span
                                        class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
                                        :class="hasVisitors ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'"
                                    >
                                        <i :class="hasVisitors ? 'fa-solid fa-users' : 'fa-regular fa-building'" class="text-xs"></i>
                                        {{ hasVisitors ? t('calendar.new.reservationType.visit') : t('calendar.new.reservationType.internal') }}
                                    </span>
                                </div>

                                <!-- Section B: Customer Information (visit type only) -->
                                <div v-if="hasVisitors" class="border-t border-surface-200 dark:border-surface-700 pt-4">
                                    <div class="flex items-center justify-between mb-3">
                                        <span class="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                                            {{ t('calendar.new.customerInfo') }}
                                        </span>
                                        <Button icon="fa-solid fa-pen" text rounded size="small" :aria-label="t('edit')" @click="goToStepById('visitor')" />
                                    </div>

                                    <!-- Profile card -->
                                    <div class="rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 p-4 space-y-4">
                                        <!-- Header: type badge + name -->
                                        <div class="space-y-2">
                                            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300">
                                                <i class="fa-solid fa-tag text-[10px]"></i>
                                                {{ t(getVisitorTypeLabel(form.visitor.type)) }}
                                            </span>
                                            <template v-if="form.visitor.type === VisitorType.ErpProfessional">
                                                <div v-if="selectedErpCustomer">
                                                    <p class="font-semibold text-base leading-tight">{{ selectedErpCustomer.name }}</p>
                                                    <p class="text-xs text-surface-400 mt-0.5">{{ selectedErpCustomer.code }}</p>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <p class="font-semibold text-base leading-tight">
                                                    {{ [form.visitor.firstName, form.visitor.lastName].filter(Boolean).join(' ') || '-' }}
                                                </p>
                                            </template>
                                        </div>

                                        <!-- Data grid -->
                                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                            <!-- Email (all types) -->
                                            <div>
                                                <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.email') }}</p>
                                                <p class="text-sm font-medium text-surface-700 dark:text-surface-200 truncate">{{ form.visitor.email || '-' }}</p>
                                            </div>

                                            <!-- NonProfessional extras -->
                                            <template v-if="form.visitor.type === VisitorType.NonProfessional">
                                                <div v-if="form.visitor.role">
                                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.role') }}</p>
                                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.visitor.role }}</p>
                                                </div>
                                                <div v-if="form.visitor.activitySector">
                                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.activitySector') }}</p>
                                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.visitor.activitySector }}</p>
                                                </div>
                                            </template>

                                            <!-- NewProfessional extras -->
                                            <template v-if="form.visitor.type === VisitorType.NewProfessional">
                                                <div v-if="form.visitor.country">
                                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.country') }}</p>
                                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.visitor.country.name }}</p>
                                                </div>
                                                <div v-if="form.visitor.commercialSegment">
                                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.commercialSegment') }}</p>
                                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.visitor.commercialSegment.name }}</p>
                                                </div>
                                                <div v-if="form.visitor.businessName">
                                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.businessName') }}</p>
                                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.visitor.businessName }}</p>
                                                </div>
                                                <div v-if="form.visitor.functionaryName">
                                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.functionary') }}</p>
                                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.visitor.functionaryName }}</p>
                                                </div>
                                                <div v-if="form.visitor.agentName">
                                                    <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.agent') }}</p>
                                                    <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.visitor.agentName }}</p>
                                                </div>
                                            </template>

                                            <!-- ErpProfessional: purchase group -->
                                            <div v-if="form.visitor.type === VisitorType.ErpProfessional && form.visitor.representsPurchaseGroup">
                                                <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.visitors.representsPurchaseGroup') }}</p>
                                                <p class="text-sm font-medium text-green-600 dark:text-green-400"><i class="fa-solid fa-check"></i></p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Section C: Reservation Details -->
                                <div class="border-t border-surface-200 dark:border-surface-700 pt-4">
                                    <div class="flex items-center justify-between mb-3">
                                        <span class="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                                            {{ t('calendar.new.reservationDetails') }}
                                        </span>
                                        <Button icon="fa-solid fa-pen" text rounded size="small" :aria-label="t('edit')" @click="goToStepById('details')" />
                                    </div>

                                    <div class="rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 p-4 space-y-4">
                                        <!-- Title — prominent -->
                                        <p class="font-semibold text-base leading-tight">{{ form.title || '-' }}</p>

                                        <!-- Data grid -->
                                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                                            <div>
                                                <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.summary.date') }}</p>
                                                <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.date ? formatSummaryDate(form.date) : '-' }}</p>
                                            </div>
                                            <div>
                                                <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.summary.attendees') }}</p>
                                                <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.attendees || 0 }}</p>
                                            </div>
                                            <div v-if="form.description" class="col-span-2">
                                                <p class="text-xs text-surface-400 mb-0.5">{{ t('calendar.new.reservation.description') }}</p>
                                                <p class="text-sm font-medium text-surface-700 dark:text-surface-200">{{ form.description }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- ===== END LEFT PANEL ===== -->

                            <!-- ===== RIGHT PANEL: PrimeVue Timeline ===== -->
                            <div>
                                <div class="flex items-center justify-between mb-3">
                                    <span class="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                                        {{ t('calendar.new.resourcesAndSlots') }}
                                    </span>
                                    <Button icon="fa-solid fa-pen" text rounded size="small" :aria-label="t('edit')" @click="goToStepById('resources')" />
                                </div>

                                <div class="relative">
                                    <!-- Full-height connecting line, centered on the dot column -->
                                    <div class="absolute inset-y-0 left-1/2 -translate-x-px w-0.5 bg-surface-200 dark:bg-surface-700"></div>

                                    <div v-for="resourceId in sortedSelectedResourceIds" :key="resourceId" class="relative grid items-start gap-x-4 py-3" style="grid-template-columns: 1fr auto 1fr">
                                        <!-- Left: time slot -->
                                        <div class="flex items-center justify-end gap-1.5 text-sm font-medium tabular-nums text-surface-700 dark:text-surface-200 mt-0.5">
                                            <span>{{ resourceSlotConfigs.get(resourceId)?.startTime || '--:--' }} – {{ resourceSlotConfigs.get(resourceId)?.endTime || '--:--' }}</span>
                                            <i class="fa-regular fa-clock text-surface-400 text-xs"></i>
                                        </div>
                                        <!-- Center: colored dot -->
                                        <div class="relative z-10 w-4 h-4 rounded-full ring-2 ring-white dark:ring-surface-900 shrink-0 mt-0.5" :style="{ backgroundColor: '#' + getResourceColor(resourceId) }"></div>
                                        <!-- Right: resource name + type + custom fields -->
                                        <div class="min-w-0">
                                            <p class="font-semibold truncate mb-0.5">{{ getResourceName(resourceId) }}</p>
                                            <p v-if="getResourceType(resourceId)" class="text-sm truncate text-surface-500 dark:text-surface-400">{{ getResourceType(resourceId) }}</p>

                                            <!-- Custom fields -->
                                            <div v-if="resourceSlotConfigs.get(resourceId)?.customFields?.length" class="mt-2 space-y-1">
                                                <div v-for="cf in resourceSlotConfigs.get(resourceId)!.customFields" :key="cf.customFieldId">
                                                    <div v-if="typeof cf.value === 'boolean'" class="flex items-center gap-1.5 text-xs">
                                                        <i :class="cf.value ? 'fa-regular fa-square-check text-green-600 dark:text-green-400' : 'fa-regular fa-square text-surface-400'" class="text-sm"></i>
                                                        <span v-tooltip="getCustomFieldDescription(cf.customFieldId)" class="font-medium text-surface-600 dark:text-surface-300">{{ getCustomFieldTitle(cf.customFieldId) }}</span>
                                                    </div>
                                                    <p v-if="typeof cf.value === 'boolean' && cf.notes" class="text-xs text-surface-400 ml-5">{{ cf.notes }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- ===== END RIGHT PANEL ===== -->
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Card Footer -->
            <div class="border-t border-surface-200 shrink-0 px-1 pt-3">
                <!-- Mandatory fields hint -->
                <p v-if="currentStepId !== 'type' && currentStepId !== 'summary'" class="text-center mb-4 text-sm sm:text-md">
                    <i18n-t keypath="calendar.new.mandatoryFieldsHint" :char="'char'">
                        <template #char><span class="text-red-500">*</span></template>
                    </i18n-t>
                </p>

                <!-- Return to review (small screens: own centered row above nav) -->
                <div v-if="cameFromSummary && !isLastStep" class="flex justify-center mb-2 sm:hidden">
                    <Button :label="t('calendar.new.reviewAndConfirm')" icon="fa-solid fa-clipboard-check" severity="secondary" @click="returnToSummary" :disabled="!canProceedFromCurrentStep" />
                </div>

                <div class="flex items-center justify-between gap-2">
                    <Button v-if="!isFirstStep" :label="t('calendar.new.prev')" icon="fa-solid fa-arrow-left" severity="secondary" outlined @click="prevStep" class="shrink-0" />
                    <div v-else></div>

                    <div class="flex items-center gap-1 sm:gap-2 justify-end">
                        <!-- Return to review (large screens: inline) -->
                        <div v-if="cameFromSummary && !isLastStep" class="hidden sm:block">
                            <Button :label="t('calendar.new.reviewAndConfirm')" icon="fa-solid fa-clipboard-check" severity="secondary" @click="returnToSummary" :disabled="!canProceedFromCurrentStep" />
                        </div>
                        <template v-if="isLastStep">
                            <Button :label="isEditMode ? t('calendar.edit.update') : t('calendar.new.save')" icon="fa-solid fa-check" :disabled="!isFormValid" :loading="isSaving" @click="submit" />
                        </template>
                        <Button v-else :label="t('calendar.new.next')" icon="fa-solid fa-arrow-right" iconPos="right" :disabled="!canProceedFromCurrentStep" @click="handleNext" />
                    </div>
                </div>
            </div>
        </template>
    </div>

    <!-- Switch type confirmation dialog -->
    <Dialog v-model:visible="switchTypeDialogVisible" modal :draggable="false" :closable="true" :style="{ width: '28rem', maxWidth: '90vw' }">
        <div class="flex items-start gap-4">
            <div class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <i class="fa-solid fa-triangle-exclamation text-amber-600"></i>
            </div>
            <div class="flex-1">
                <p v-for="(msg, i) in switchTypeDialogMessages" :key="i" :class="{ 'mt-2': i > 0 }">
                    {{ msg }}
                </p>
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2 p-5">
                <Button :label="t('cancel')" severity="secondary" outlined @click="switchTypeDialogVisible = false" />
                <Button :label="t('calendar.new.switchType.confirmAccept')" severity="primary" @click="onSwitchTypeConfirm" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import type { ResourceSlotConfig } from '@/components/reservation/ResourcesSlotsForm.vue';
import ResourcesSlotsForm from '@/components/reservation/ResourcesSlotsForm.vue';
import VisitorDataForm from '@/components/reservation/VisitorDataForm.vue';
import { useReservationValidation } from '@/composables/useReservationValidation';
import { useSlotDialog } from '@/composables/useSlotDialog';
import { useVisitorForm } from '@/composables/useVisitorForm';
import { ReservationStatus, ReservationType, type Reservation, type ReservationResource } from '@/lib/types/reservation';
import type { SlotCapacityInfo } from '@/lib/types/slotCapacity';
import { UserAuthorizations } from '@/lib/types/user';
import type { Visitor } from '@/lib/types/visitor';
import { VisitorType } from '@/lib/types/visitor';
import { getErrorMessage, getVisitorTypeLabel } from '@/plugins/i18n/enums';
import { useAuthStore } from '@/store/auth';
import { useCommercialSegmentsStore } from '@/store/commercialSegment';
import { useCountriesStore } from '@/store/country';
import { useCustomFieldsStore } from '@/store/customField';
import { useReservationsStore } from '@/store/reservation';
import { useResourcesStore } from '@/store/resource';
import { msToResourceLocalCalendarDate, msToResourceLocalTimeString, resourceLocalDateTimeToMs } from '@/utils/date';
import { useToast } from 'primevue/usetoast';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const toast = useToast();
const resourcesStore = useResourcesStore();
const reservationsStore = useReservationsStore();
const customFieldsStore = useCustomFieldsStore();
const countriesStore = useCountriesStore();
const commercialSegmentsStore = useCommercialSegmentsStore();
const authStore = useAuthStore();

// Authorization checks
const canManageOwnReservations = computed(() => authStore.user?.permissions.includes(UserAuthorizations.ManageOwnReservations) ?? false);
const canManageInternalReservations = computed(() => authStore.user?.permissions.includes(UserAuthorizations.ManageInternalReservations) ?? false);

const loading = ref(true);
const isSaving = ref(false);
const isEditMode = computed(() => !!route.params.id);
const editingReservationId = ref<string | null>(null);
const isLoadingEditData = ref(false);

// Reservation type (has visitors or not)
const hasVisitors = ref(true);

const form = reactive({
    title: '',
    description: '',
    attendees: 0,
    date: null as Date | null,
    visitor: {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        type: VisitorType.NonProfessional,
        erpCode: null,
        representsPurchaseGroup: null,
        commercialSegment: null,
        functionaryId: null,
        functionaryName: null,
        agentId: null,
        agentName: null,
        country: null,
        businessName: null,
        role: null,
        activitySector: null
    } as Visitor,
    selectedResourceIds: [] as string[]
});

// Per-resource slot configurations
const resourceSlotConfigs = reactive(new Map<string, ResourceSlotConfig>());

// Track which fields have been touched for validation
const touched = reactive({
    title: false,
    attendees: false,
    visitorType: false,
    visitorFirstName: false,
    visitorLastName: false,
    visitorEmail: false,
    visitorCountry: false,
    visitorCommercialSegment: false,
    erpCustomer: false
});

// Slot validation conflict error
const slotConflictError = ref<string | null>(null);
const isValidatingSlot = ref(false);

// Selected ERP customer
const selectedErpCustomer = ref<any>(null);

// Use composables
const { validation, visitorValidationErrors } = useReservationValidation(
    touched,
    hasVisitors,
    computed(() => form.title),
    computed(() => form.description),
    computed(() => form.attendees),
    computed(() => form.visitor),
    selectedErpCustomer
);

const visitorForm = useVisitorForm(form.visitor, selectedErpCustomer);
const slotDialog = useSlotDialog();

const slotCapacities = ref<Map<string, SlotCapacityInfo>>(new Map());
// One generation counter per resource, bumped on every checkResourceCapacity call. The badge is written after an
// await, so without it a slow response for attendees = 2 can land after a fast one for attendees = 5 and leave the
// stale pair on screen — including a stale hasError, which feeds hasCapacityErrors and therefore the Save gate.
const capacityRequestSeq = new Map<string, number>();

// ==================== STEPPER ====================

const currentStepIndex = ref(0);
const transitionName = ref('step-slide-left');
const cameFromSummary = ref(false);

const steps = computed(() => {
    const s: Array<{ id: string; label: string; icon: string }> = [];
    if (!isEditMode.value) {
        s.push({ id: 'type', label: t('calendar.new.reservationType.title'), icon: 'fa-solid fa-tag' });
    }
    if (hasVisitors.value) {
        s.push({ id: 'visitor', label: t('calendar.new.customerInfo'), icon: 'fa-solid fa-user' });
    }
    s.push({ id: 'details', label: t('calendar.new.reservationDetails'), icon: 'fa-regular fa-calendar' });
    s.push({ id: 'resources', label: t('calendar.new.resourcesAndSlots'), icon: 'fa-solid fa-cubes' });
    s.push({ id: 'summary', label: t('calendar.new.reviewAndConfirm'), icon: 'fa-solid fa-clipboard-check' });
    return s;
});

const currentStep = computed(() => steps.value[currentStepIndex.value]);
const currentStepId = computed(() => currentStep.value?.id ?? '');
const isFirstStep = computed(() => currentStepIndex.value === 0);
const isLastStep = computed(() => currentStepIndex.value === steps.value.length - 1);

const canProceedFromCurrentStep = computed(() => {
    switch (currentStepId.value) {
        case 'type':
            return true;
        case 'visitor': {
            const visitor = form.visitor;
            if (visitor.type === VisitorType.ErpProfessional) {
                return selectedErpCustomer.value !== null && visitor.email.trim() !== '';
            } else if (visitor.type === VisitorType.NewProfessional) {
                return visitor.firstName.trim() !== '' && visitor.lastName.trim() !== '' && visitor.email.trim() !== '' && visitor.country !== null && visitor.commercialSegment !== null;
            } else {
                return visitor.firstName.trim() !== '' && visitor.lastName.trim() !== '' && visitor.email.trim() !== '';
            }
        }
        case 'details':
            return form.title.trim() !== '' && form.title.trim().length <= 255 && (form.description?.trim()?.length ?? 0) <= 255 && form.attendees > 0;
        case 'resources': {
            if (!form.date) return false;
            if (form.selectedResourceIds.length === 0) return false;
            if (hasCapacityErrors.value || isCheckingCapacity.value) return false;
            if (slotConflictError.value || isValidatingSlot.value) return false;
            // TZ-02: the step gate asks buildSlotsArray directly rather than re-deriving the times,
            // so "the wizard says this step is complete" and "the payload contains a slot for every
            // selected resource" cannot disagree. It used to call parseTimeString with no zone,
            // i.e. browser-local: a range like 02:30-03:00 on a spring-forward date orders
            // correctly on the browser's clock but inverts on the resource's, so the gate let the
            // operator advance while buildSlotsArray's endMs > startMs filter silently dropped that
            // resource's slot from the payload.
            return buildSlotsArray().length === form.selectedResourceIds.length;
        }
        default:
            return true;
    }
});

function nextStep() {
    if (currentStepIndex.value < steps.value.length - 1) {
        transitionName.value = 'step-slide-left';
        currentStepIndex.value++;
    }
}

function prevStep() {
    if (currentStepIndex.value > 0) {
        transitionName.value = 'step-slide-right';
        currentStepIndex.value--;
    }
}

function goToStepById(id: string) {
    const index = steps.value.findIndex((s) => s.id === id);
    if (index >= 0) {
        cameFromSummary.value = currentStepId.value === 'summary';
        transitionName.value = index < currentStepIndex.value ? 'step-slide-right' : 'step-slide-left';
        currentStepIndex.value = index;
    }
}

function touchCurrentStepFields() {
    if (currentStepId.value === 'visitor') {
        touched.visitorFirstName = true;
        touched.visitorLastName = true;
        touched.visitorEmail = true;
        touched.visitorCountry = true;
        touched.visitorCommercialSegment = true;
        touched.visitorAgent = true;
        touched.erpCustomer = true;
    } else if (currentStepId.value === 'details') {
        touched.title = true;
        touched.attendees = true;
    }
}

function handleNext() {
    touchCurrentStepFields();
    if (canProceedFromCurrentStep.value) {
        nextStep();
    }
}

function returnToSummary() {
    touchCurrentStepFields();
    if (canProceedFromCurrentStep.value) {
        cameFromSummary.value = false;
        const summaryIndex = steps.value.findIndex((s) => s.id === 'summary');
        if (summaryIndex >= 0) {
            transitionName.value = 'step-slide-left';
            currentStepIndex.value = summaryIndex;
        }
    }
}

function stepCircleClasses(index: number): string {
    if (index < currentStepIndex.value) return 'bg-blue-600 text-white shadow-sm';
    if (index === currentStepIndex.value) return 'border-2 border-blue-600 text-blue-600 bg-blue-50';
    return 'border-2 border-surface-300 text-surface-400';
}

function stepLabelClasses(index: number): string {
    if (index <= currentStepIndex.value) return 'text-blue-600';
    return 'text-surface-400';
}

function getResourceName(resourceId: string): string {
    return resourcesStore.resources.find((r) => r.id === resourceId)?.name || resourceId;
}

function getResourceColor(resourceId: string): string {
    return resourcesStore.resources.find((r) => r.id === resourceId)?.color || '8B5CF6';
}

function getResourceType(resourceId: string): string {
    return resourcesStore.resources.find((r) => r.id === resourceId)?.type?.name || '';
}

function getCustomFieldTitle(fieldId: string): string {
    return customFieldsStore.customFields.find((cf) => cf.id === fieldId)?.title || fieldId;
}

function getCustomFieldDescription(fieldId: string): string | null {
    return customFieldsStore.customFields.find((cf) => cf.id === fieldId)?.description || null;
}

// ==================== BUSINESS LOGIC ====================

// Build slots array from current form state (for validation/submit).
//
// TZ-02 / D-11: each selected resource's HH:mm resolves against ITS OWN timezone, never the
// operator's browser. 09:00 in Rome and 09:00 in Tokyo are different instants, and that is the
// intended semantics: the operator is typing the time at each resource. The shared form.date
// supplies only the calendar y/m/d, which is likewise interpreted in each resource's zone.
// Anchoring every resource to the first selected resource's zone was rejected — a Tokyo room
// showing 09:00 but booked at 17:00 local is exactly the confusion this phase removes — as was
// blocking mixed-timezone selection, which would be a new restriction on behaviour that is legal
// today. A resource whose zone cannot be resolved falls back to browser-local inside
// resourceLocalDateTimeToMs (D-09), so it stays bookable rather than dropping out silently.
//
// The filter below is load-bearing: resourceLocalDateTimeToMs returns null for a missing or
// unparseable time, and `?? 0` turns that into a zero the filter discards, so an incomplete row is
// never sent as an epoch-zero slot.
function buildSlotsArray(): ReservationResource[] {
    if (!form.date) return [];
    return form.selectedResourceIds
        .map((resourceId) => {
            const config = resourceSlotConfigs.get(resourceId);
            const startTime = config?.startTime;
            const endTime = config?.endTime;
            const resource = resourcesStore.getResourceById(resourceId);
            const timezoneId = resource?.timezone?.id ?? null;
            return {
                resourceId,
                startMs: resourceLocalDateTimeToMs(timezoneId, form.date, startTime ?? null) ?? 0,
                endMs: resourceLocalDateTimeToMs(timezoneId, form.date, endTime ?? null) ?? 0,
                customFields: (config?.customFields || []).filter((cf) => cf.value === true)
            };
        })
        .filter((slot) => slot.startMs > 0 && slot.endMs > 0 && slot.endMs > slot.startMs);
}

// Check capacity for a single resource (TLINE-02). This is the ONLY implementation: the useSlotCapacity composable
// that used to hold a near-verbatim, never-instantiated copy has been deleted, and SlotCapacityInfo now lives in
// @/lib/types/slotCapacity. Do not reintroduce a second copy.
async function checkResourceCapacity(resourceId: string) {
    // Bumped before ANY early return: a later invocation must invalidate an in-flight one even when it exits
    // early, otherwise a response already on the wire repopulates an entry this call just deleted.
    const seq = (capacityRequestSeq.get(resourceId) ?? 0) + 1;
    capacityRequestSeq.set(resourceId, seq);

    // D-10: no attendee-count gate — the badge must render as soon as a date and a time range exist.
    if (!form.date) {
        slotCapacities.value.delete(resourceId);
        return;
    }
    const config = resourceSlotConfigs.get(resourceId);
    const startTime = config?.startTime;
    const endTime = config?.endTime;
    // Every non-success exit deletes the entry: returning without deleting left the previous badge on screen
    // after the operator cleared a time or inverted the range, and D-10 removed the attendees early-exit that
    // used to hide most of those invocations.
    if (!startTime || !endTime) {
        slotCapacities.value.delete(resourceId);
        return;
    }

    const resource = resourcesStore.resources.find((r) => r.id === resourceId);
    // D-09: no badge for exclusive (0) or single-occupancy (1) resources — ROADMAP SC5.
    if (!resource || resource.capacity <= 1) {
        slotCapacities.value.delete(resourceId);
        return;
    }

    // TZ-02 / D-11: the capacity window MUST be resolved on the resource's own clock, exactly as
    // buildSlotsArray resolves the slot this badge gates. The lookup above used to sit BELOW this
    // conversion, and the conversion was browser-local — so for a resource whose offset differs from
    // the operator's, the wizard queried occupancy for a different window than the one being booked
    // and could report "fits" for a slot the server then rejects. Same timezoneId expression as
    // buildSlotsArray, so the two can no longer drift.
    const timezoneId = resource.timezone?.id ?? null;
    const startMs = resourceLocalDateTimeToMs(timezoneId, form.date, startTime);
    const endMs = resourceLocalDateTimeToMs(timezoneId, form.date, endTime);
    if (startMs === null || endMs === null || startMs >= endMs) {
        slotCapacities.value.delete(resourceId);
        return;
    }
    const startDate = new Date(startMs);
    const endDate = new Date(endMs);

    slotCapacities.value.set(resourceId, {
        loading: true,
        total: resource.capacity,
        available: 0,
        requested: form.attendees,
        hasError: false,
        errorMessage: null
    });

    try {
        const result = await reservationsStore.getOccupiedCapacityForResource(resource, startDate, endDate, editingReservationId.value);
        // A newer request for this resource started while this one was in flight — its answer is the current one.
        if (capacityRequestSeq.get(resourceId) !== seq) return;

        const available = result.available;
        // D-10: an error requires a stated attendee count, so the neutral state never reads as red.
        const hasError = form.attendees > 0 && form.attendees > available;
        slotCapacities.value.set(resourceId, {
            loading: false,
            total: result.total,
            available,
            requested: form.attendees,
            hasError,
            errorMessage: hasError ? t('calendar.new.capacity.notEnoughSpace', { requested: form.attendees, available, total: result.total }) : null
        });
    } catch {
        if (capacityRequestSeq.get(resourceId) !== seq) return;

        slotCapacities.value.set(resourceId, {
            loading: false,
            total: resource.capacity,
            available: 0,
            requested: form.attendees,
            hasError: true,
            errorMessage: t('calendar.new.capacity.checkError')
        });
    }
}

async function checkAllCapacities() {
    for (const resourceId of form.selectedResourceIds) {
        await checkResourceCapacity(resourceId);
    }
}

// Every deletion of a capacity entry OUTSIDE checkResourceCapacity must also advance the resource's generation
// counter, otherwise a response already in flight passes the seq check and resurrects the entry — and because
// hasCapacityErrors iterates every map entry while checkAllCapacities only refreshes selected resources, a
// resurrected hasError would block Save forever with no visible badge (the resource's card is gone).
function invalidateCapacityEntry(resourceId: string) {
    capacityRequestSeq.set(resourceId, (capacityRequestSeq.get(resourceId) ?? 0) + 1);
    slotCapacities.value.delete(resourceId);
}

const hasCapacityErrors = computed(() => {
    for (const [_, info] of slotCapacities.value) {
        if (info.hasError) return true;
    }
    return false;
});

const isCheckingCapacity = computed(() => {
    for (const [_, info] of slotCapacities.value) {
        if (info.loading) return true;
    }
    return false;
});

// Touch all fields for validation on submit
function touchAllFields() {
    Object.keys(touched).forEach((key) => {
        (touched as any)[key] = true;
    });
}

// Touch a specific field
function touchField(fieldName: string) {
    if (fieldName in touched) {
        (touched as any)[fieldName] = true;
    }
}

// Summary date formatting
function formatSummaryDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

// Smart type switching
const switchTypeDialogVisible = ref(false);
const switchTypeDialogMessages = ref<string[]>([]);
const switchTypeDialogAction = ref<(() => void) | null>(null);

function onSwitchTypeConfirm() {
    switchTypeDialogVisible.value = false;
    switchTypeDialogAction.value?.();
    switchTypeDialogAction.value = null;
}

function hasVisitorData(): boolean {
    const v = form.visitor;
    return !!(v.firstName.trim() || v.lastName.trim() || v.email.trim() || v.erpCode || v.commercialSegment || v.functionaryId || v.agentId || v.country || selectedErpCustomer.value);
}

function resetVisitorData() {
    form.visitor.id = '';
    form.visitor.firstName = '';
    form.visitor.lastName = '';
    form.visitor.email = '';
    form.visitor.type = VisitorType.NonProfessional;
    form.visitor.erpCode = null;
    form.visitor.representsPurchaseGroup = null;
    form.visitor.commercialSegment = null;
    form.visitor.functionaryId = null;
    form.visitor.functionaryName = null;
    form.visitor.agentId = null;
    form.visitor.agentName = null;
    form.visitor.country = null;
    form.visitor.businessName = null;
    form.visitor.role = null;
    form.visitor.activitySector = null;
    selectedErpCustomer.value = null;

    touched.visitorFirstName = false;
    touched.visitorLastName = false;
    touched.visitorEmail = false;
    touched.visitorCountry = false;
    touched.visitorCommercialSegment = false;
    touched.erpCustomer = false;
}

function performSwitchToInternal() {
    resetVisitorData();
    hasVisitors.value = false;
}

function performSwitchToExternal() {
    hasVisitors.value = true;
}

function switchToExternal() {
    if (hasVisitors.value) return;
    performSwitchToExternal();
}

function switchToInternal() {
    if (!hasVisitors.value) return;

    const visitorDataExists = hasVisitorData();

    if (visitorDataExists) {
        switchTypeDialogMessages.value = [t('calendar.new.switchType.loseVisitorData')];
        switchTypeDialogAction.value = () => performSwitchToInternal();
        switchTypeDialogVisible.value = true;
    } else {
        performSwitchToInternal();
    }
}

// Resource selection change handler
function handleResourceSelectionChange(newIds: string[]) {
    const oldIds = form.selectedResourceIds;
    // Remove configs for deselected resources
    for (const oldId of oldIds) {
        if (!newIds.includes(oldId)) {
            resourceSlotConfigs.delete(oldId);
            invalidateCapacityEntry(oldId);
        }
    }
    // Initialize configs for newly added resources
    for (const newId of newIds) {
        if (!oldIds.includes(newId)) {
            resourceSlotConfigs.set(newId, {
                startTime: null,
                endTime: null,
                customFields: []
            });
            // Fetch availability data for new resource
            slotDialog.fetchResourceTimelineData(newId);
            if (form.date) {
                checkResourceCapacity(newId);
            }
        }
    }
    form.selectedResourceIds = newIds;
}

// Per-resource config update
function handleResourceSlotConfigUpdate(resourceId: string, config: ResourceSlotConfig) {
    resourceSlotConfigs.set(resourceId, config);
}

// Remove resource
function handleRemoveResource(resourceId: string) {
    form.selectedResourceIds = form.selectedResourceIds.filter((id) => id !== resourceId);
    resourceSlotConfigs.delete(resourceId);
    invalidateCapacityEntry(resourceId);
}

// Form validity
const sortedSelectedResourceIds = computed(() =>
    [...form.selectedResourceIds].sort((a, b) => {
        const ta = resourceSlotConfigs.get(a)?.startTime ?? '';
        const tb = resourceSlotConfigs.get(b)?.startTime ?? '';
        return ta.localeCompare(tb);
    })
);

const isFormValid = computed(() => {
    const baseValid =
        form.title.trim() !== '' &&
        form.title.trim().length <= 255 &&
        (form.description?.trim()?.length ?? 0) <= 255 &&
        form.attendees > 0 &&
        form.date !== null &&
        form.selectedResourceIds.length > 0 &&
        !hasCapacityErrors.value &&
        !isCheckingCapacity.value &&
        !slotConflictError.value &&
        !isValidatingSlot.value;

    // Check all resources have valid times. Same rule as the step gate above and for the same
    // reason: ask buildSlotsArray, so the submit gate and the payload it enables cannot disagree.
    const allTimesValid = buildSlotsArray().length === form.selectedResourceIds.length;

    if (!allTimesValid) return false;

    if (hasVisitors.value) {
        const visitor = form.visitor;
        if (visitor.type === VisitorType.ErpProfessional) {
            return baseValid && selectedErpCustomer.value !== null && visitor.email.trim() !== '';
        } else if (visitor.type === VisitorType.NewProfessional) {
            return baseValid && visitor.firstName.trim() !== '' && visitor.lastName.trim() !== '' && visitor.email.trim() !== '' && visitor.country !== null && visitor.commercialSegment !== null;
        } else {
            return baseValid && visitor.firstName.trim() !== '' && visitor.lastName.trim() !== '' && visitor.email.trim() !== '';
        }
    }
    return baseValid;
});

// ==================== WATCHERS ====================

// Watch visitor type changes
watch(
    () => form.visitor.type,
    (newType, oldType) => {
        if (isLoadingEditData.value) return;
        visitorForm.handleVisitorTypeChange(newType, oldType);
    }
);

// Debounced capacity check — shared across all triggers to avoid API spam
let capacityDebounceTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedCheckAllCapacities() {
    if (capacityDebounceTimer) clearTimeout(capacityDebounceTimer);
    capacityDebounceTimer = setTimeout(() => {
        if (form.selectedResourceIds.length > 0) {
            checkAllCapacities();
        }
    }, 500);
}

// Watch date changes to refresh capacities
watch(
    () => form.date,
    () => {
        if (form.date) debouncedCheckAllCapacities();
    }
);

// Watch attendees to recheck capacity
watch(() => form.attendees, debouncedCheckAllCapacities);

// Watch only time changes in resource configs for capacity rechecks (not custom fields)
const slotTimesFingerprint = computed(() =>
    form.selectedResourceIds
        .map((id) => {
            const config = resourceSlotConfigs.get(id);
            return `${id}:${config?.startTime}:${config?.endTime}`;
        })
        .join('|')
);

watch(slotTimesFingerprint, debouncedCheckAllCapacities);

// Clamp step index when steps array changes (e.g. type switch removes visitor step)
watch(steps, (newSteps) => {
    if (currentStepIndex.value >= newSteps.length) {
        currentStepIndex.value = Math.max(0, newSteps.length - 1);
    }
});

// ==================== LIFECYCLE ====================

/** Load reservation for editing */
async function loadReservationForEdit(reservationId: string) {
    try {
        loading.value = true;

        let reservation = reservationsStore.reservations.find((r: { id: string }) => r.id === reservationId);

        if (!reservation) {
            toast.add({
                severity: 'error',
                summary: 'Errore',
                detail: 'Prenotazione non trovata',
                life: 3000
            });
            router.push({ name: 'calendar' });
            return;
        }

        editingReservationId.value = reservationId;
        isLoadingEditData.value = true;

        // Determine reservation type
        const isExternalReservation = reservation.visitors && reservation.visitors.length > 0;
        hasVisitors.value = isExternalReservation;

        // Populate basic form fields
        form.title = reservation.title;
        form.description = reservation.description || '';
        form.attendees = reservation.attendees || 0;

        // Extract date from first slot.
        //
        // D-13: edit mode and create mode are the SAME wizard and must not diverge — reopening a
        // saved reservation has to show the same resource-local times the wizard would have written
        // for it. This site had the identical browser-local defect the prefill above did.
        //
        // D-11: form.date is shared across resources that may sit on different clocks, so it takes
        // the calendar date of the first slot's OWN resource — the same single-answer rule the
        // prefill path uses — and buildSlotsArray reinterprets that y/m/d per resource on save.
        if (reservation.slots.length > 0) {
            const firstSlot = reservation.slots[0];
            const firstSlotTimezoneId = resourcesStore.getResourceById(firstSlot.resourceId)?.timezone?.id ?? null;
            form.date = msToResourceLocalCalendarDate(firstSlotTimezoneId, firstSlot.startMs);
        }

        // Extract unique resource IDs
        const uniqueResourceIds = [...new Set(reservation.slots.map((s: any) => s.resourceId))];
        form.selectedResourceIds = uniqueResourceIds as string[];

        // Set per-resource configs
        for (const resourceId of uniqueResourceIds) {
            const resourceSlots = reservation.slots.filter((s: any) => s.resourceId === resourceId);
            if (resourceSlots.length > 0) {
                const slot = resourceSlots[0];
                // D-11: each resource reads on its OWN clock, so the zone is resolved INSIDE this
                // loop. Hoisting it above the loop and reusing the first resource's zone would show a
                // Tokyo room the Rome hour — exactly the confusion this phase removes.
                const slotTimezoneId = resourcesStore.getResourceById(resourceId as string)?.timezone?.id ?? null;

                resourceSlotConfigs.set(resourceId as string, {
                    startTime: msToResourceLocalTimeString(slotTimezoneId, slot.startMs),
                    endTime: msToResourceLocalTimeString(slotTimezoneId, slot.endMs),
                    customFields: slot.customFields ? slot.customFields.map((cf: any) => ({ ...cf })) : []
                });

                slotDialog.fetchResourceTimelineData(resourceId as string);
            }
        }

        // Set visitor data for external reservations
        if (isExternalReservation && reservation.visitors && reservation.visitors.length > 0) {
            const visitor = reservation.visitors[0];
            form.visitor = { ...visitor };

            if (visitor.type === VisitorType.NewProfessional) {
                if (visitor.country?.id) {
                    const foundCountry = countriesStore.countries.find((c) => c.id === visitor.country?.id);
                    form.visitor.country = foundCountry || null;
                }
                if (visitor.commercialSegment?.id) {
                    const foundSegment = commercialSegmentsStore.commercialSegments.find((cs) => cs.id === visitor.commercialSegment?.id);
                    form.visitor.commercialSegment = foundSegment || null;
                }
            }

            if (visitor.type === VisitorType.ErpProfessional) {
                selectedErpCustomer.value = {
                    code: visitor.erpCode || '',
                    name: `${visitor.firstName} ${visitor.lastName}`.trim(),
                    email: visitor.email
                };
            }
        }

        await nextTick();
        isLoadingEditData.value = false;
    } catch (error) {
        console.error('Error loading reservation:', error);
        isLoadingEditData.value = false;
        toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel caricamento della prenotazione',
            life: 3000
        });
        router.push({ name: 'calendar' });
    } finally {
        loading.value = false;
    }
}

function cancelEdit() {
    router.push({ name: 'calendar' });
}

onMounted(async () => {
    // Set default reservation type based on user permissions
    if (canManageOwnReservations.value) {
        hasVisitors.value = true;
    } else if (canManageInternalReservations.value) {
        hasVisitors.value = false;
    }

    await Promise.all([resourcesStore.fetchAll(), countriesStore.fetchAll(), commercialSegmentsStore.fetchAll()]);

    if (isEditMode.value) {
        const reservationId = route.params.id as string;
        await loadReservationForEdit(reservationId);
    } else {
        // Handle prefill from calendar query params
        const startQ = route.query.start;
        const endQ = route.query.end;
        const resourceIdsQ = route.query.resourceIds;

        const startMs = typeof startQ === 'string' ? Number(startQ) : null;
        const endMs = typeof endQ === 'string' ? Number(endQ) : null;
        const resourceIds = typeof resourceIdsQ === 'string' && resourceIdsQ ? resourceIdsQ.split(',').filter(Boolean) : [];

        if (startMs && endMs && resourceIds.length > 0 && !Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs > startMs) {
            // D-13 / SC4: the drag-select's instants are converted HERE, not in Calendar.vue. The
            // calendar pushes raw epoch-ms plus resourceIds and stays untouched — it is the correct
            // seam precisely because it does not know which resource's clock to read those ms on
            // until the wizard resolves the first selected one. Converting them browser-local made
            // the wizard contradict the grid row the operator had just dragged.
            const firstResourceId = resourceIds[0];
            const tzId = resourcesStore.getResourceById(firstResourceId)?.timezone?.id ?? null;

            // D-11: form.date is ONE shared value across resources that may sit on different clocks.
            // Taking it from the FIRST selected resource's calendar date is the only defensible single
            // answer; buildSlotsArray then reinterprets that y/m/d in each resource's own zone on save.
            // The asymmetry is deliberate, not an oversight.
            form.date = msToResourceLocalCalendarDate(tzId, startMs);

            form.selectedResourceIds = resourceIds;

            // Pre-fill slot times only for the first selected resource
            resourceSlotConfigs.set(firstResourceId, {
                startTime: msToResourceLocalTimeString(tzId, startMs),
                endTime: msToResourceLocalTimeString(tzId, endMs),
                customFields: []
            });

            slotDialog.fetchResourceTimelineData(firstResourceId);
        }

        loading.value = false;
    }
});

const submit = async () => {
    touchAllFields();

    if (!isFormValid.value) {
        toast.add({
            severity: 'warn',
            summary: t('calendar.new.validation.formInvalid'),
            detail: t('calendar.new.validation.formInvalidDetail'),
            life: 5000
        });
        return;
    }

    if (isSaving.value) return;
    isSaving.value = true;

    try {
        const visitors = hasVisitors.value ? [form.visitor] : [];
        const slots = buildSlotsArray();

        const isExternal = hasVisitors.value;

        const reservationData: Reservation = {
            id: editingReservationId.value || '',
            title: form.title,
            description: form.description,
            attendees: form.attendees,
            visitors: visitors,
            slots: slots,
            status: isExternal ? ReservationStatus.Planned : ReservationStatus.Completed,
            type: isExternal ? ReservationType.External : ReservationType.Internal,
            createdBy: null as any
        };

        // Validate first
        const validationError = await reservationsStore.validateOne(reservationData);
        if (validationError) {
            toast.add({
                severity: 'error',
                summary: t('error.title'),
                detail: getErrorMessage(validationError, t),
                life: 5000
            });
            isSaving.value = false;
            return;
        }

        if (isEditMode.value && editingReservationId.value) {
            const error = await reservationsStore.updateOne(reservationData);
            if (error) {
                toast.add({
                    severity: 'error',
                    summary: t('error.title'),
                    detail: getErrorMessage(error, t),
                    life: 5000
                });
            } else {
                toast.add({
                    severity: 'success',
                    summary: 'Prenotazione aggiornata',
                    detail: 'La prenotazione è stata aggiornata con successo',
                    life: 3000
                });
            }
        } else {
            const error = await reservationsStore.createOne(reservationData);
            if (error) {
                toast.add({
                    severity: 'error',
                    summary: t('error.title'),
                    detail: getErrorMessage(error, t),
                    life: 5000
                });
            } else {
                toast.add({
                    severity: 'success',
                    summary: 'Prenotazione creata',
                    detail: 'La prenotazione è stata creata con successo',
                    life: 3000
                });
            }
        }

        router.push({ name: 'calendar' });
    } catch (error: any) {
        console.error('Error submitting form:', error);

        const errorMessage = error.response?.data?.errorCode
            ? getErrorMessage({ errorCode: error.response.data.errorCode, message: error.response.data.message }, t)
            : isEditMode.value
              ? t('calendar.new.error.update')
              : t('calendar.new.error.create');

        toast.add({
            severity: 'error',
            summary: t('error.title'),
            detail: errorMessage,
            life: 5000
        });
    } finally {
        isSaving.value = false;
    }
};
</script>

<style scoped>
.step-slide-left-enter-active,
.step-slide-left-leave-active,
.step-slide-right-enter-active,
.step-slide-right-leave-active {
    transition: all 0.25s ease-out;
}

.step-slide-left-enter-from {
    opacity: 0;
    transform: translateX(24px);
}

.step-slide-left-leave-to {
    opacity: 0;
    transform: translateX(-24px);
}

.step-slide-right-enter-from {
    opacity: 0;
    transform: translateX(-24px);
}

.step-slide-right-leave-to {
    opacity: 0;
    transform: translateX(24px);
}
</style>
